import { ListBox, ListBoxItem, type Key } from 'react-aria-components';

import styles from './QueueList.module.css';

export interface QueueListItem {
  id: string;
  /** Daily ticket, shown in the identifier face, e.g. "042". */
  ticket: string | null;
  name: string;
  /** e.g. "F, 34 y". */
  details: string;
  /** Whole minutes waited, right-aligned. */
  waitingMinutes: number;
  /** "Called", "Being seen": the entry's state in words, when not simply waiting. */
  state?: string;
  /** Why the patient is where they are in the list: the triage category and flags. */
  markers?: readonly string[];
}

export interface QueueListProps {
  /** Accessible name, e.g. "Patients waiting at Triage". */
  label: string;
  /** In the order patients are seen; the list never reorders them itself. */
  items: readonly QueueListItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function minutes(value: number): string {
  if (value < 60) return `${String(value)} min`;
  const hours = Math.floor(value / 60);
  return `${String(hours)} h ${String(value % 60)} min`;
}

/**
 * A service point's queue (DESIGN.md §6.3): 48px rows, waiting time right-aligned in
 * tabular figures, the selected row marked with a 3px ink bar and wash fill. Arrow keys
 * move, Enter selects.
 */
export function QueueList({ label, items, selectedId, onSelect }: QueueListProps) {
  return (
    <ListBox
      aria-label={label}
      className={styles.list}
      items={items}
      selectionMode="single"
      selectedKeys={selectedId === null ? [] : [selectedId]}
      disallowEmptySelection={false}
      onSelectionChange={(keys) => {
        if (keys === 'all') return;
        const [first] = [...keys] as Key[];
        if (first !== undefined) onSelect(String(first));
      }}
    >
      {(item) => (
        <ListBoxItem
          id={item.id}
          className={styles.row}
          textValue={`${item.ticket ?? ''} ${item.name}`.trim()}
        >
          <span className={styles.ticket}>{item.ticket ?? ''}</span>
          <span className={styles.who}>
            <span className={styles.name}>{item.name}</span>
            <span className={styles.details}>
              {item.details}
              {item.state === undefined ? null : <span className={styles.state}>{item.state}</span>}
              {(item.markers ?? []).map((m) => (
                <span key={m} className={styles.marker}>
                  {m}
                </span>
              ))}
            </span>
          </span>
          <span className={styles.wait}>{minutes(item.waitingMinutes)}</span>
        </ListBoxItem>
      )}
    </ListBox>
  );
}
